// src/components/TokenTicker.js
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// ✅ Fetch data for your token
const DEXSCREENER_URL =
  'https://api.dexscreener.com/latest/dex/tokens/0xdD3f2d1B0EC5ea81CDf70e79C713D35EF8d530E1';

// ✅ Link where users can buy
const BUY_URL =
  'https://dexscreener.com/bsc/0xb6d72ccd34928314228fa72a8e80800dd9907795';

const TickerWrapper = styled.div`
  width: 100%;
  background: radial-gradient(circle at 0% 0%, #22c55e20, #020617 60%);
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
  padding: 0.5rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Text',
    'Inter', sans-serif;
  font-size: 0.85rem;
  color: #e5e7eb;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const TokenBadge = styled.span`
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.4);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.75rem;
`;

const Value = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 500;
`;

const Label = styled.span`
  opacity: 0.65;
  margin-right: 0.25rem;
`;

const blink = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #22c55e;
  margin-right: 0.4rem;
  animation: ${blink} 1.4s infinite ease-in-out;
`;

const Change = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: ${({ positive, negative }) =>
    positive ? '#22c55e' : negative ? '#ef4444' : '#e5e7eb'};
`;

const SmallSeparator = styled.span`
  width: 1px;
  height: 18px;
  background: rgba(148, 163, 184, 0.4);
  border-radius: 999px;
`;

const BuyButton = styled.a`
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(56, 189, 248, 0.8);
  background: radial-gradient(circle at 0% 0%, #0ea5e980, #0369a1 80%);
  color: #f9fafb;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.7);
  cursor: pointer;
  transition: transform 0.12s ease-out, box-shadow 0.12s ease-out,
    background 0.12s ease-out;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 25px rgba(15, 118, 110, 0.45);
    background: radial-gradient(circle at 0% 0%, #22c55e90, #0f766e 80%);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.8);
  }
`;

const TokenTicker = () => {
  const [price, setPrice] = useState(null);
  const [change24h, setChange24h] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let intervalId;

    const fetchData = async () => {
      try {
        const res = await fetch(DEXSCREENER_URL);
        if (!res.ok) throw new Error('Failed to fetch');

        const data = await res.json();

        const pair = data?.pairs?.[0];
        if (!pair) return;

        const priceUsd = pair.priceUsd ? Number(pair.priceUsd) : null;
        const change = pair.priceChange?.h24 ?? null;

        setPrice(priceUsd);
        setChange24h(change != null ? Number(change) : null);
        setLoading(false);
      } catch (err) {
        console.error('Dexscreener fetch error:', err);
        setLoading(false);
      }
    };

    fetchData();
    intervalId = setInterval(fetchData, 30000);

    return () => clearInterval(intervalId);
  }, []);

  const formattedPrice =
    price != null ? `$${price.toFixed(price < 0.01 ? 6 : 4)}` : '--';

  const formattedChange =
    change24h != null
      ? `${change24h >= 0 ? '+' : ''}${change24h.toFixed(2)}%`
      : '--';

  return (
    <TickerWrapper>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Dot />
        <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Live on-chain</span>
      </div>

      <SmallSeparator />

      <TokenBadge>$TGEN / USDT</TokenBadge>

      <div>
        <Label>Price</Label>
        <Value>{loading ? 'Loading…' : formattedPrice}</Value>
      </div>

      <SmallSeparator />

      <div>
        <Label>24h Change</Label>
        <Change positive={change24h > 0} negative={change24h < 0}>
          {loading ? '—' : formattedChange}
        </Change>
      </div>

      <SmallSeparator />

      <BuyButton href={BUY_URL} target="_blank" rel="noopener noreferrer">
        Buy now
        <span style={{ fontSize: '0.9em' }}>↗</span>
      </BuyButton>
    </TickerWrapper>
  );
};

export default TokenTicker;
